// Blog Landing Page
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import BlogFilterBar from './BlogFilterBar';
import BlogMobileFilterPanel from './BlogMobileFilterPanel';
import BlogMobileFilterButton from './BlogMobileFilterButton';
import BlogSearch from './BlogSearch';
import SelectedFilters from '../filter/SelectedFilters';
import Pagination from '../pagination/Pagination';
import Lazyload from '../../modules/lazyload';
import {
  appendQueryToUrl,
  buildBlogFilterQuery,
  sortArticlesByDate,
} from '../../modules/blog-filter-utils';

const KLAVIYO_FOOTER_MOUNT = '.footer .newsletter [class*="klaviyo-form-"]';

const dedupeKlaviyoFooterEmbed = () => {
  const mount = document.querySelector(KLAVIYO_FOOTER_MOUNT);
  if (!mount || mount.childElementCount <= 1) {
    return;
  }

  while (mount.childElementCount > 1) {
    mount.removeChild(mount.lastElementChild);
  }
};

const scheduleKlaviyoFooterDedupe = () => {
  window.requestAnimationFrame(() => {
    dedupeKlaviyoFooterEmbed();
    window.setTimeout(dedupeKlaviyoFooterEmbed, 0);
    window.setTimeout(dedupeKlaviyoFooterEmbed, 250);
  });
};

const scrollDocumentToTop = () => {
  if (document.activeElement && typeof document.activeElement.blur === 'function') {
    document.activeElement.blur();
  }

  window.scrollTo(0, 0);
  window.requestAnimationFrame(() => {
    window.scrollTo(0, 0);
  });
};

class BlogArticles extends Component {
  constructor(props) {
    super(props);

    this.articleItemsSource = props.articleItems;
    this.sortedItems = sortArticlesByDate(props.articleItems);

    this.state = {
      pageOfItems: [],
    };

    this.onChangePage = this.onChangePage.bind(this);
  }

  componentDidMount() {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    scrollDocumentToTop();

    Lazyload();
    this.syncListingUrl();
  }

  componentDidUpdate(prevProps) {
    Lazyload();

    const searchChanged = prevProps.searchText !== this.props.searchText;
    this.syncListingUrl({ skipFooterDedupe: searchChanged });

    if (prevProps.selectedFilters !== this.props.selectedFilters) {
      scrollDocumentToTop();
    }
  }

  onChangePage(pageOfItems, options = {}) {
    this.setState({ pageOfItems }, () => {
      if (options.scroll) {
        scrollDocumentToTop();
      }
    });
  }

  syncSortedItems(articleItems) {
    if (this.articleItemsSource !== articleItems) {
      this.articleItemsSource = articleItems;
      this.sortedItems = sortArticlesByDate(articleItems);
    }

    return this.sortedItems;
  }

  syncListingUrl(options = {}) {
    const { filters, searchText, selectedFilters } = this.props;
    const query = buildBlogFilterQuery(filters, selectedFilters, searchText);
    const nextUrl = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    if (nextUrl !== currentUrl) {
      window.history.replaceState(null, '', nextUrl);
      if (!options.skipFooterDedupe) {
        scheduleKlaviyoFooterDedupe();
      }
    }
  }

  render() {
    const {
      filterToggle,
      filterToggleClick,
      filters,
      articleItems,
      buttonClick,
      closeAllDropdowns,
      filterClick,
      searchSubmit,
      searchText,
      selectedFilters,
      applyBlogFilters,
      closeFilterPanel,
      removeFilter,
      clearFilters,
    } = this.props;

    const sortedItems = this.syncSortedItems(articleItems);
    const hasActiveFilters = Object.keys(selectedFilters)
      .some(key => Array.isArray(selectedFilters[key]) && selectedFilters[key].length > 0);
    const showClearAll = hasActiveFilters;
    const filterQuery = buildBlogFilterQuery(filters, selectedFilters, searchText);

    return (
      <div className="article-container overflow-visible">
        <BlogMobileFilterPanel
          filters={filters}
          selectedFilters={selectedFilters}
          isOpen={filterToggle}
          onApply={applyBlogFilters}
          onClose={closeFilterPanel}
        />
        <section className="filter article-category-filter blog-filters-section pb-3 pt-3">
          <div className="container">
            <div className="blog-filters-toolbar d-flex align-items-center">
              <BlogMobileFilterButton
                isOpen={filterToggle}
                onClick={filterToggleClick}
              />
              {showClearAll ? (
                <button
                  type="button"
                  className="btn btn-link text-dark text-decoration-underline p-0 border-0 blog-clear-filters blog-clear-filters-mobile text-nowrap fw-normal lh-base"
                  onClick={() => clearFilters()}
                >
                  Clear All
                </button>
              ) : null}
              <div className="blog-desktop-filters">
                <BlogFilterBar
                  filters={filters}
                  buttonClick={buttonClick}
                  closeAllDropdowns={closeAllDropdowns}
                  filterClick={filterClick}
                  showClearAll={showClearAll}
                  clearFilters={clearFilters}
                />
              </div>
              <BlogSearch searchText={searchText} searchSubmit={searchSubmit} />
            </div>
            <SelectedFilters
              selectedFilters={selectedFilters}
              removeFilter={removeFilter}
              clearFilters={clearFilters}
              clearLabel="Clear Filter"
              pillStyle
            />
          </div>
        </section>
        <section className="article-list overflow-visible">
          <div className="container">
            {(articleItems.length > 0)
            ? (
              <div className="article-wrapper border-top pt-3">
                <div className="row all-articles row-cols-1 row-cols-sm-2 row-cols-md-3 g-4">
                  {this.state.pageOfItems.map(article => (
                    <div key={article.id} className="col">
                      <a
                        className="card rounded-0 border-0"
                        href={appendQueryToUrl(article.articleLink, filterQuery)}
                      >
                        <img className="card-img-top object-fit-cover vh-30 lazy" data-src={article.primaryImage} alt={article.primaryImageAltTag} />
                        <div className="loader-placeholder">
                          <svg id="loading" className="loading" data-name="loading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300">
                            <title>Loading</title>
                            <path className="loading-path" d="M250,149A100,100,0,1,0,150,250c1.68,0,3.34-.06,5-.14V212.29c-1.65.14-3.32.21-5,.21A62.5,62.5,0,1,1,212.47,149H200l30,30,30-30Z" />
                          </svg>
                        </div>
                        <div className="card-body p-0 pt-2">
                          <h5 className="article-category mb-0 text-secondary mb-2">
                            {[
                              ...(article.articleTypes || []),
                            ].map((category, index, arr) => (
                              <span
                                key={`${category.id}-${category.name}`}
                                className="article-category-text text-lowercase fw-medium"
                              >
                                {category.name}
                                {index === arr.length - 1 ? '' : ', '}
                              </span>
                            ))}
                          </h5>
                          <h3 className="text-evergreen card-title">{article.articleTitle}</h3>
                          <p className="article-date m-0">
                            {new Date(article.publishDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })}
                          </p>
                          <p className="link link-secondary">{this.props.articlesListLinkText}</p>
                        </div>
                      </a>
                    </div>
                  ))}
                  <Pagination items={sortedItems} onChangePage={this.onChangePage} />
                </div>
              </div>
            ) : (
              <div className="results-message-container blog-no-results mb-5 border-top pt-3 pb-5">
                <p className="mb-0">{this.props.noResultsMessage}</p>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }
}

BlogArticles.propTypes = {
  articleItems: PropTypes.arrayOf(PropTypes.object),
  noResultsMessage: PropTypes.string,
  articlesListLinkText: PropTypes.string,
  filterToggle: PropTypes.bool,
  searchText: PropTypes.string,
  filterToggleClick: PropTypes.func.isRequired,
  applyBlogFilters: PropTypes.func.isRequired,
  closeFilterPanel: PropTypes.func.isRequired,
  buttonClick: PropTypes.func.isRequired,
  closeAllDropdowns: PropTypes.func.isRequired,
  filterClick: PropTypes.func.isRequired,
  searchSubmit: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  selectedFilters: PropTypes.shape(PropTypes.object),
  filters: PropTypes.shape(PropTypes.object),
};

export default BlogArticles;
